import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button, Card, ErrorBanner, Spinner } from "../components/ui.js";
import { useCategories } from "../hooks/useInventory.js";
import { api } from "../lib/api.js";
import type { Category } from "../types.js";

interface WhatnotCatalogRow {
  id: string;
  title: string;
  productId: string;
  sku: string | null;
  productUrl: string | null;
  observedPrice: string | null;
  state: string;
  syncError: string | null;
  canResumePublish: boolean;
  itemId: string | null;
  itemDeleted: boolean;
  lastSeenAt: string;
  candidates: Array<{ id: string; name: string; sku: string | null }>;
}

interface TransferCandidate {
  id: string;
  name: string;
  sku: string | null;
  forSalePrice: string | null;
  quantity: number;
  categoryName: string | null;
  mapped: boolean;
}

interface WhatnotDimensionProfile {
  id: string;
  name?: string;
  active?: boolean;
}

interface WhatnotOrderRow {
  id: string;
  providerOrderId: string;
  status: string;
  createdAtProvider: string | null;
  total: string | null;
  currency: string | null;
  syncedAt: string;
  snapshot: {
    items?: Array<{
      title: string;
      sku: string | null;
      quantity: number;
      unitPrice: string | null;
      currency: string | null;
      farnsworthItemId: string | null;
      farnsworthItemName: string | null;
    }>;
  };
}

/** Depth-first list of every category in the tree, for flat dropdowns. */
function flattenCategories(nodes: Category[]): Category[] {
  return nodes.flatMap((node) => [node, ...flattenCategories(node.children ?? [])]);
}

export function WhatnotPage() {
  const queryClient = useQueryClient();
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [dimensionProfileId, setDimensionProfileId] = useState("");
  const [selectedListingIds, setSelectedListingIds] = useState<Record<string, boolean>>({});
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [rowCategoryIds, setRowCategoryIds] = useState<Record<string, string>>({});
  const status = useQuery({
    queryKey: ["whatnot", "status"],
    queryFn: () => api.get<{ data: { configured: boolean } }>("/whatnot/status"),
    staleTime: 60_000,
  });
  const catalog = useQuery({
    queryKey: ["whatnot", "catalog"],
    queryFn: () => api.get<{ data: WhatnotCatalogRow[] }>("/whatnot/catalog"),
    staleTime: 30_000,
  });
  const candidates = useQuery({
    queryKey: ["whatnot", "transfer-candidates"],
    queryFn: () => api.get<{ data: TransferCandidate[] }>("/whatnot/transfer-candidates"),
    staleTime: 30_000,
  });
  const orders = useQuery({
    queryKey: ["whatnot", "orders"],
    queryFn: () => api.get<{ data: WhatnotOrderRow[] }>("/whatnot/orders"),
    staleTime: 60_000,
  });
  const dimensionProfiles = useQuery({
    queryKey: ["whatnot", "dimension-profiles"],
    queryFn: () => api.get<{ data: WhatnotDimensionProfile[] }>("/whatnot/dimension-profiles"),
    enabled: false,
    staleTime: 5 * 60_000,
  });
  const categories = useCategories();
  const sync = useMutation({
    mutationFn: () => api.post<{ data: { imported: number; soldHidden: number; stockUnknown: number; lookupFailed: number; message: string } }>("/whatnot/catalog/sync"),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["whatnot", "catalog"] });
      void queryClient.invalidateQueries({ queryKey: ["whatnot", "status"] });
    },
  });
  const link = useMutation({
    mutationFn: ({ listingId, itemId }: { listingId: string; itemId: string }) =>
      api.post(`/whatnot/catalog/${listingId}/link`, { itemId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["whatnot", "catalog"] }),
  });
  const importListing = useMutation({
    mutationFn: (body: { listingId: string; categoryId?: string }) =>
      api.post<{ data: { item: { id: string; name: string } } }>(`/whatnot/catalog/${body.listingId}/import`, { categoryId: body.categoryId || undefined }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["whatnot", "catalog"] }),
  });
  type ImportBatchResult = { imported: number; failed: number; results: Array<{ listingId: string; ok: boolean; error?: string }> };
  const importBulk = useMutation({
    mutationFn: (body: { listingIds: string[]; categoryId?: string }) =>
      api.post<{ data: ImportBatchResult }>("/whatnot/catalog/import", { listingIds: body.listingIds, categoryId: body.categoryId || undefined }),
    onSuccess: () => {
      setSelectedListingIds({});
      void queryClient.invalidateQueries({ queryKey: ["whatnot", "catalog"] });
      void queryClient.invalidateQueries({ queryKey: ["whatnot", "transfer-candidates"] });
    },
  });
  const syncPrice = useMutation({
    mutationFn: (listingId: string) => api.post<{ data: WhatnotCatalogRow }>(`/whatnot/catalog/${listingId}/sync-price`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["whatnot", "catalog"] }),
  });
  const syncDetails = useMutation({
    mutationFn: (listingId: string) => api.post(`/whatnot/catalog/${listingId}/sync-details`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["whatnot", "catalog"] }),
  });
  const syncStock = useMutation({
    mutationFn: (listingId: string) => api.post(`/whatnot/catalog/${listingId}/sync-stock`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["whatnot", "catalog"] }),
  });
  const retryRemoval = useMutation({
    mutationFn: (listingId: string) => api.post(`/whatnot/catalog/${listingId}/retry-removal`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["whatnot", "catalog"] }),
  });
  const publish = useMutation({
    mutationFn: (itemId: string) => api.post(`/whatnot/publish/${itemId}`, { dimensionProfileId: dimensionProfileId || "resume" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["whatnot", "catalog"] });
      void queryClient.invalidateQueries({ queryKey: ["whatnot", "transfer-candidates"] });
    },
  });
  const syncOrders = useMutation({
    mutationFn: () => api.post<{ data: { synced: number; message: string } }>("/whatnot/orders/sync", {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["whatnot", "orders"] }),
  });

  // Rows Farnsworth does not have yet: no linked inventory item and no match candidates.
  const importableIds = (catalog.data?.data ?? [])
    .filter((listing) => !listing.itemId && listing.candidates.length === 0)
    .map((listing) => listing.id);
  const selectedIds = importableIds.filter((id) => selectedListingIds[id]);
  const allSelected = importableIds.length > 0 && selectedIds.length === importableIds.length;
  const categoryOptions = flattenCategories(categories.data ?? []);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Whatnot listings</h1>
          <p className="mt-1 text-sm text-slate-400">Refresh the catalog on demand, review possible matches, and link existing Whatnot products to Farnsworth inventory.</p>
        </div>
        <Button type="button" disabled={!status.data?.data.configured || sync.isPending} onClick={() => sync.mutate()}>
          {sync.isPending ? "Refreshing catalog…" : "Refresh Whatnot catalog"}
        </Button>
      </header>
      {status.data && !status.data.data.configured ? <Card><p className="text-sm text-amber-200">Configure WHATNOT_API_TOKEN on the API server to connect the catalog.</p></Card> : null}
      {sync.isError ? <ErrorBanner message={(sync.error as Error).message} /> : null}
      {link.isError ? <ErrorBanner message={(link.error as Error).message} /> : null}
      {importListing.isError ? <ErrorBanner message={(importListing.error as Error).message} /> : null}
      {importBulk.isError ? <ErrorBanner message={(importBulk.error as Error).message} /> : null}
      {importBulk.data ? (
        <div className="space-y-1">
          <p className="text-sm text-emerald-300">Added {importBulk.data.data.imported} product(s) to Farnsworth inventory.{importBulk.data.data.failed ? ` ${importBulk.data.data.failed} skipped.` : ""}</p>
          {importBulk.data.data.results.some((result) => !result.ok) ? (
            <ul className="space-y-1">
              {importBulk.data.data.results.filter((result) => !result.ok).map((result) => (
                <li key={result.listingId} className="text-xs text-amber-300">
                  {catalog.data?.data.find((row) => row.id === result.listingId)?.title ?? result.listingId}: {result.error}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {syncPrice.isError ? <ErrorBanner message={(syncPrice.error as Error).message} /> : null}
      {syncDetails.isError ? <ErrorBanner message={(syncDetails.error as Error).message} /> : null}
      {syncStock.isError ? <ErrorBanner message={(syncStock.error as Error).message} /> : null}
      {retryRemoval.isError ? <ErrorBanner message={(retryRemoval.error as Error).message} /> : null}
      {dimensionProfiles.isError ? <ErrorBanner message={(dimensionProfiles.error as Error).message} /> : null}
      {publish.isError ? <ErrorBanner message={(publish.error as Error).message} /> : null}
      {syncOrders.isError ? <ErrorBanner message={(syncOrders.error as Error).message} /> : null}
      {catalog.isError ? <ErrorBanner message={(catalog.error as Error).message} /> : null}
      {sync.data ? (
        <p className="text-sm text-emerald-300">
          {sync.data.data.message} {sync.data.data.imported} products processed
          {sync.data.data.soldHidden ? `, ${sync.data.data.soldHidden} sold hidden` : ""}
          {sync.data.data.lookupFailed ? `, ${sync.data.data.lookupFailed} stock checks failed` : ""}.
        </p>
      ) : null}
      {syncOrders.data ? <p className="text-sm text-emerald-300">{syncOrders.data.data.message} {syncOrders.data.data.synced} orders processed.</p> : null}
      {catalog.isLoading ? <Spinner label="Loading saved Whatnot catalog…" /> : null}
      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-100">Send inventory to Whatnot</h2>
            <p className="mt-1 text-sm text-slate-400">Nothing is sent until you choose an item and click Send. Price comes from its Farnsworth for-sale price.</p>
          </div>
          <Button type="button" variant="secondary" disabled={!status.data?.data.configured || dimensionProfiles.isFetching} onClick={() => void dimensionProfiles.refetch()}>
            {dimensionProfiles.isFetching ? "Loading shipping profiles…" : "Load shipping profiles"}
          </Button>
        </div>
        {dimensionProfiles.data?.data.length ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-slate-300">Shipping profile
              <select className="mt-1 block min-h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-slate-100" value={dimensionProfileId} onChange={(event) => setDimensionProfileId(event.target.value)}>
                <option value="">Choose a Whatnot shipping profile…</option>
                {dimensionProfiles.data.data.filter((profile) => profile.active !== false).map((profile) => <option key={profile.id} value={profile.id}>{profile.name ?? profile.id}</option>)}
              </select>
            </label>
            <p className="self-end pb-2 text-xs text-slate-400">New items are published as Buy It Now listings in the Whatnot profile shop.</p>
          </div>
        ) : null}
        {candidates.isLoading ? <Spinner label="Loading eligible inventory…" /> : null}
        {candidates.isError ? <ErrorBanner message={(candidates.error as Error).message} /> : null}
        {candidates.data?.data.slice(0, 100).map((item) => (
          <Card key={item.id} className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium text-slate-100">{item.name}</div>
              <div className="mt-1 text-xs text-slate-400">{item.categoryName ?? "No category"} · Qty {item.quantity} · For sale {item.forSalePrice ?? "not set"}{item.sku ? ` · SKU ${item.sku}` : ""}</div>
              {!item.mapped ? <p className="mt-1 text-xs text-amber-300">Map this category to Whatnot first.</p> : null}
            </div>
            <Button type="button" variant="secondary" disabled={!item.mapped || !item.forSalePrice || !dimensionProfileId || publish.isPending} onClick={() => publish.mutate(item.id)}>
              {publish.isPending && publish.variables === item.id ? "Sending…" : "Send to Whatnot"}
            </Button>
          </Card>
        ))}
        {!candidates.isLoading && candidates.data?.data.length === 0 ? <Card><p className="text-sm text-slate-400">No available inventory is ready to send.</p></Card> : null}
      </section>
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950 p-3">
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={allSelected}
              disabled={!importableIds.length}
              onChange={(event) => {
                const next: Record<string, boolean> = { ...selectedListingIds };
                for (const id of importableIds) {
                  if (event.target.checked) next[id] = true;
                  else delete next[id];
                }
                setSelectedListingIds(next);
              }}
            />
            Select all not in Farnsworth ({importableIds.length})
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Category for this batch"
              className="min-h-10 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100"
              value={bulkCategoryId}
              onChange={(event) => setBulkCategoryId(event.target.value)}
            >
              <option value="">Use category mapping…</option>
              {categoryOptions.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}{category.whatnotCategoryId !== null ? ` · Whatnot ${category.whatnotCategoryId}` : ""}
                </option>
              ))}
            </select>
            <Button type="button" disabled={!selectedIds.length || importBulk.isPending} onClick={() => importBulk.mutate({ listingIds: selectedIds, categoryId: bulkCategoryId || undefined })}>
              {importBulk.isPending ? "Adding…" : `Add ${selectedIds.length} to Farnsworth inventory`}
            </Button>
          </div>
        </div>
        {catalog.data?.data.map((listing) => (
          <Card key={listing.id} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="font-medium text-slate-100">{listing.title}</div>
              <div className="mt-1 text-xs text-slate-400">Whatnot ID {listing.productId}{listing.sku ? ` · SKU ${listing.sku}` : ""}{listing.observedPrice ? ` · Whatnot price ${listing.observedPrice}` : ""} · Seen {new Date(listing.lastSeenAt).toLocaleString()}</div>
              {listing.productUrl ? <a href={listing.productUrl} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-indigo-300 hover:text-indigo-200">Open on Whatnot</a> : null}
            </div>
            {listing.itemId ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className={`shrink-0 rounded-full border px-3 py-1 text-xs ${listing.state === "needs_attention" ? "border-amber-800 text-amber-300" : listing.state === "sold" ? "border-slate-700 text-slate-300" : "border-emerald-800 text-emerald-300"}`}>
                  {listing.state === "removed" ? "Removed from Whatnot" : listing.state === "sold" ? "Sold on Whatnot" : listing.state === "publishing" ? "Publishing to Whatnot" : listing.state === "needs_attention" ? "Sync needs attention" : "Linked to Farnsworth"}
                </span>
                {listing.state === "removed" ? (
                  <Button type="button" variant="secondary" disabled={!dimensionProfileId || publish.isPending} onClick={() => publish.mutate(listing.itemId!)}>
                    {publish.isPending && publish.variables === listing.itemId ? "Republishing…" : "Republish to Whatnot"}
                  </Button>
                ) : listing.canResumePublish ? (
                  <Button type="button" variant="secondary" disabled={publish.isPending} onClick={() => publish.mutate(listing.itemId!)}>
                    {publish.isPending && publish.variables === listing.itemId ? "Resuming…" : "Resume Whatnot publish"}
                  </Button>
                ) : listing.itemDeleted ? (
                  <Button type="button" variant="secondary" disabled={retryRemoval.isPending} onClick={() => retryRemoval.mutate(listing.id)}>
                    {retryRemoval.isPending && retryRemoval.variables === listing.id ? "Retrying removal…" : "Retry Whatnot removal"}
                  </Button>
                ) : (
                  <>
                    <Button type="button" variant="secondary" disabled={syncPrice.isPending} onClick={() => syncPrice.mutate(listing.id)}>
                      {syncPrice.isPending && syncPrice.variables === listing.id ? "Syncing…" : "Push for-sale price"}
                    </Button>
                    <Button type="button" variant="secondary" disabled={syncDetails.isPending} onClick={() => syncDetails.mutate(listing.id)}>
                      {syncDetails.isPending && syncDetails.variables === listing.id ? "Syncing…" : "Sync listing details"}
                    </Button>
                    <Button type="button" variant="secondary" disabled={syncStock.isPending} onClick={() => syncStock.mutate(listing.id)}>
                      {syncStock.isPending && syncStock.variables === listing.id ? "Syncing…" : "Set Whatnot stock from Farnsworth"}
                    </Button>
                  </>
                )}
                {listing.syncError ? <span className="basis-full text-xs text-amber-300">{listing.syncError}</span> : null}
              </div>
            ) : listing.candidates.length ? (
              <div className="flex w-full gap-2 sm:w-auto">
                <select aria-label={`Match for ${listing.title}`} className="min-h-10 min-w-0 flex-1 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 sm:w-72" value={selections[listing.id] ?? ""} onChange={(event) => setSelections((current) => ({ ...current, [listing.id]: event.target.value }))}>
                  <option value="">Choose matching inventory item…</option>
                  {listing.candidates.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.name}{candidate.sku ? ` · ${candidate.sku}` : ""}</option>)}
                </select>
                <Button type="button" variant="secondary" disabled={!selections[listing.id] || link.isPending} onClick={() => link.mutate({ listingId: listing.id, itemId: selections[listing.id]! })}>Link</Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-400">
                  <input
                    type="checkbox"
                    checked={Boolean(selectedListingIds[listing.id])}
                    onChange={(event) => setSelectedListingIds((current) => ({ ...current, [listing.id]: event.target.checked }))}
                  />
                  Select
                </label>
                <select
                  aria-label={`Category for ${listing.title}`}
                  className="min-h-10 min-w-0 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 sm:w-56"
                  value={rowCategoryIds[listing.id] ?? ""}
                  onChange={(event) => setRowCategoryIds((current) => ({ ...current, [listing.id]: event.target.value }))}
                >
                  <option value="">Use category mapping…</option>
                  {categoryOptions.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}{category.whatnotCategoryId !== null ? ` · Whatnot ${category.whatnotCategoryId}` : ""}
                    </option>
                  ))}
                </select>
                <Button type="button" variant="secondary" disabled={importListing.isPending} onClick={() => importListing.mutate({ listingId: listing.id, categoryId: rowCategoryIds[listing.id] || undefined })}>
                  {importListing.isPending && importListing.variables?.listingId === listing.id ? "Importing…" : "Add to Farnsworth inventory"}
                </Button>
              </div>
            )}
          </Card>
        ))}
        {!catalog.isLoading && !catalog.data?.data.length ? <Card><p className="text-sm text-slate-400">No Whatnot products are cached yet. Refresh the catalog to begin.</p></Card> : null}
      </section>
      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-100">Recent Whatnot orders</h2>
            <p className="mt-1 text-sm text-slate-400">Refresh on demand. Orders and lines are cached here without buyer details; matched items are shown for review.</p>
          </div>
          <Button type="button" variant="secondary" disabled={!status.data?.data.configured || syncOrders.isPending} onClick={() => syncOrders.mutate()}>
            {syncOrders.isPending ? "Refreshing orders…" : "Refresh latest orders"}
          </Button>
        </div>
        {orders.isLoading ? <Spinner label="Loading cached Whatnot orders…" /> : null}
        {orders.isError ? <ErrorBanner message={(orders.error as Error).message} /> : null}
        {orders.data?.data.map((order) => (
          <Card key={order.id} className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-medium text-slate-100">Order {order.providerOrderId}</div>
              <span className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300">{order.status}</span>
            </div>
            <p className="text-xs text-slate-400">{order.createdAtProvider ? new Date(order.createdAtProvider).toLocaleString() : "Date unavailable"} · Total {order.total ?? "—"} {order.currency ?? ""} · Cached {new Date(order.syncedAt).toLocaleString()}</p>
            <ul className="space-y-1 border-t border-slate-800 pt-2">
              {(order.snapshot.items ?? []).map((line, index) => (
                <li key={`${order.id}-${index}`} className="flex flex-wrap justify-between gap-2 text-sm">
                  <span className="text-slate-200">{line.title}{line.sku ? ` · ${line.sku}` : ""} × {line.quantity}
                    <span className={`ml-2 text-xs ${line.farnsworthItemId ? "text-emerald-300" : "text-amber-300"}`}>{line.farnsworthItemName ?? "Needs inventory match"}</span>
                  </span>
                  <span className="text-slate-400">{line.unitPrice ?? "—"} {line.currency ?? ""}</span>
                </li>
              ))}
            </ul>
          </Card>
        ))}
        {!orders.isLoading && !orders.data?.data.length ? <Card><p className="text-sm text-slate-400">No Whatnot orders cached. Use Refresh latest orders to pull current data.</p></Card> : null}
      </section>
    </div>
  );
}
